// Receivables, Payables, Customer Debits and Supplier Credits: the open bills of
// the branch's customers or suppliers, bill-wise, party-wise and by age.
//
// The figures are the ones the Bill-wise Receipt / Payment screens settle
// against (BillReceipt/GetAllCustomerBills, BillPayment/GetAllSupBills): per
// bill, balance = bill amount - (settled + discount). The backend has no single
// outstanding endpoint, so the service asks once per party - see
// getOutstandingBills and docs/BACKEND-COMPLETE.md.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import { ReportScreen } from "../../../components/report";
import { dateOf, dmy, groupSum, money, num, sumBy } from "../../../components/report/reportFormat";
import { getOutstandingBills } from "../../../services/ReportApiService";
import LoadingUtils from "../../../core/utils/LoadingUtils";

const BANDS = [
  { v: "0-30", t: "0–30 days", k: "b0", max: 30 },
  { v: "31-60", t: "31–60 days", k: "b31", max: 60 },
  { v: "61-90", t: "61–90 days", k: "b61", max: 90 },
  { v: "90+", t: "Over 90 days", k: "b90", max: Infinity },
];
const bandOf = (days) => BANDS.find((b) => days <= b.max) || BANDS[BANDS.length - 1];

// SR / PR bills are returns: they reduce what is owed
const isReturnType = (t) => /^(SR|PR)$/i.test(String(t || "").trim());

const OutstandingReport = ({ party = "customer", title, subtitle, defaultTab = "bill" }) => {
  const { branch } = useSelector((state) => state.branchlist);
  const isCustomer = party === "customer";
  const who = isCustomer ? "Customer" : "Supplier";

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!branch?.guid) return;
    setLoading(true);
    try {
      const res = await getOutstandingBills({ party, branch });
      setData(res.rows);
      if (res.failed)
        toast.error(`${res.failed} of ${res.parties} ${who.toLowerCase()}s could not be read`);
    } catch (error) {
      console.error(error?.response?.data?.Message || "Failed to load open bills");
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [branch, party, who]);

  useEffect(() => {
    load();
  }, [load]);

  /* one row per open bill */
  const rows = useMemo(() => {
    const today = dayjs().startOf("day");
    const out = [];
    data.forEach(({ party: p, bills }) => {
      // a bill comes back once per settlement line - fold them per bill number
      const byBill = {};
      bills.forEach((b) => {
        const k = b.entryNumber || b.salesMasterID || b.purchaseMasterID;
        if (!byBill[k]) byBill[k] = { ...b, amount: num(b.amount), discount: num(b.discount) };
        else {
          byBill[k].amount += num(b.amount);
          byBill[k].discount += num(b.discount);
        }
      });
      Object.values(byBill).forEach((b) => {
        const sign = isReturnType(b.transType) ? -1 : 1;
        const billAmount = num(b.netTotal);
        const settled = b.amount + b.discount;
        const open = billAmount - settled;
        if (Math.abs(open) < 0.005) return;
        const date = dateOf(b.entryDate);
        const age = date ? Math.max(0, today.diff(dayjs(date), "day")) : 0;
        const band = bandOf(age);
        out.push({
          key: `${p.guid}|${b.entryNumber}|${b.transType}`,
          date,
          billNo: b.entryNumber || "",
          invoiceNo: b.invoiceNO && b.invoiceNO !== "0" ? b.invoiceNO : "",
          type: sign < 0 ? "Return" : "Invoice",
          party: p.name || "—",
          partyGuid: p.guid,
          mobile: p.mobile || "",
          billAmount: sign * billAmount,
          settled: sign * settled,
          balance: sign * open,
          age,
          band: band.v,
          [band.k]: sign * open,
        });
      });
    });
    return out;
  }, [data]);

  /* ---------------- filters ---------------- */
  const filters = useMemo(() => {
    const seen = {};
    rows.forEach((r) => (seen[r.partyGuid] = r.party));
    return [
      {
        key: "parties",
        label: `${who}s`,
        items: Object.entries(seen)
          .map(([v, t]) => ({ v, t }))
          .sort((a, b) => a.t.localeCompare(b.t)),
        match: (r, v) => v.includes(r.partyGuid),
      },
      {
        key: "age",
        label: "Age",
        items: BANDS.map((b) => ({ v: b.v, t: b.t })),
        match: (r, v) => v.includes(r.band),
      },
    ];
  }, [rows, who]);

  const searchMatch = (r, q) =>
    [r.billNo, r.invoiceNo, r.party, r.mobile].some((s) => String(s || "").toLowerCase().includes(q)) ||
    String(r.balance).includes(q);

  /* ---------------- tabs ---------------- */
  const tabs = useMemo(() => {
    const bandCols = BANDS.map((b) => ({ k: b.k, t: b.t, type: "money" }));
    return [
      {
        id: "bill",
        label: "Bill-wise",
        defaultSort: { k: "date", d: 1 },
        rowKey: (r) => r.key,
        groupOptions: [
          { v: "party", t: who },
          { v: "band", t: "Age" },
          { v: "__month", t: "Month" },
        ],
        columns: [
          { k: "sl", t: "Sl No", type: "index" },
          { k: "date", t: "Bill Date", type: "date" },
          { k: "billNo", t: "Bill No", type: "code" },
          { k: "invoiceNo", t: isCustomer ? "Invoice No" : "Supplier Inv No", off: isCustomer },
          { k: "party", t: who, type: "avatar" },
          {
            k: "type",
            t: "Type",
            type: "pill",
            off: true,
            pill: (r) => ({ text: r.type, tone: r.type === "Return" ? "credit" : "cash" }),
          },
          { k: "billAmount", t: "Bill Amount", type: "money" },
          { k: "settled", t: isCustomer ? "Received" : "Paid", type: "money" },
          { k: "balance", t: "Balance", type: "money" },
          { k: "age", t: "Age (days)", type: "int", noSum: true },
        ],
      },
      {
        id: "party",
        label: `${who}-wise`,
        defaultSort: { k: "balance", d: -1 },
        rowKey: (r) => r.key,
        drillDown: false,
        rows: (list) =>
          groupSum(list, (r) => r.partyGuid, ["billAmount", "settled", "balance"], (r) => ({
            party: r.party,
            mobile: r.mobile,
          })).map((g) => {
            const own = list.filter((r) => r.partyGuid === g.key);
            return {
              ...g,
              oldest: own.reduce((a, r) => (!a || (r.date && r.date < a) ? r.date : a), ""),
              maxAge: own.reduce((a, r) => Math.max(a, r.age), 0),
            };
          }),
        columns: [
          { k: "party", t: who, type: "avatar" },
          { k: "mobile", t: "Mobile", off: true },
          { k: "n", t: "Open Bills", type: "int" },
          { k: "billAmount", t: "Bill Amount", type: "money" },
          { k: "settled", t: isCustomer ? "Received" : "Paid", type: "money" },
          { k: "balance", t: "Balance", type: "money" },
          { k: "oldest", t: "Oldest Bill", type: "date" },
          { k: "maxAge", t: "Oldest (days)", type: "int", noSum: true },
          { k: "share", t: "Share of Balance", type: "share", shareOf: "balance" },
        ],
      },
      {
        id: "ageing",
        label: "Ageing",
        defaultSort: { k: "balance", d: -1 },
        rowKey: (r) => r.key,
        drillDown: false,
        rows: (list) =>
          groupSum(
            list,
            (r) => r.partyGuid,
            ["balance", ...BANDS.map((b) => b.k)],
            (r) => ({ party: r.party })
          ),
        columns: [
          { k: "party", t: who, type: "avatar" },
          { k: "n", t: "Open Bills", type: "int" },
          ...bandCols,
          { k: "balance", t: "Total", type: "money" },
        ],
      },
    ];
  }, [who, isCustomer]);

  /* ---------------- KPIs ---------------- */
  const kpis = useMemo(
    () => [
      { label: "Open bills", value: (list) => list.length, format: "int" },
      {
        label: `${who}s`,
        value: (list) => new Set(list.map((r) => r.partyGuid)).size,
        format: "int",
      },
      { label: "Billed", value: (list) => sumBy(list, "billAmount") },
      { label: isCustomer ? "Received" : "Paid", value: (list) => sumBy(list, "settled") },
      {
        label: "Over 90 days",
        value: (list) => sumBy(list.filter((r) => r.band === "90+"), "balance"),
        invert: true,
      },
      { label: "Outstanding", value: (list) => sumBy(list, "balance"), hero: true },
    ],
    [who, isCustomer]
  );

  const drawerContent = (r) => ({
    title: r.party,
    subtitle: `${r.billNo} · ${dmy(r.date)}`,
    avatarKey: r.party,
    meta: [
      { k: "Type", v: r.type },
      { k: isCustomer ? "Invoice no" : "Supplier inv no", v: r.invoiceNo || "—" },
      { k: "Mobile", v: r.mobile || "—" },
      { k: "Age", v: `${r.age} days` },
    ],
    totals: [
      { l: "Bill amount", v: money(r.billAmount) },
      { l: isCustomer ? "Received" : "Paid", v: money(r.settled) },
    ],
    net: { l: "Balance", v: money(r.balance) },
  });

  return (
    <>
      <LoadingUtils loading={loading} />
      <ReportScreen
        title={title}
        subtitle={subtitle}
        unit="open bills"
        rows={rows}
        tabs={tabs}
        filters={filters}
        kpis={kpis}
        chart={{ valueKey: "balance", title: "Outstanding by bill date" }}
        searchMatch={searchMatch}
        searchPlaceholder={`Bill no, ${who.toLowerCase()}, mobile…`}
        exportName={title}
        reportNames={{
          bill: `${title} Bill Wise`,
          party: `${title} ${who} Wise`,
          ageing: `${title} Ageing`,
        }}
        onRefresh={load}
        drawerContent={drawerContent}
        defaultPreset="all"
        defaultTab={defaultTab}
      />
    </>
  );
};

export default OutstandingReport;
